import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from './Store';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_SIZE,
  DEFAULT_TABLE_FONT_SIZE,
  HEADING_FONT_SIZES,
} from './defaultTextDefaultValue';
import type { CSSProperties } from 'react';
import { DEFAULT_FONT_COLOR, DEFAULT_TABLE_HEADER_ROW_FONT_COLOR, DEFAULT_TABLE_ROW_FONT_COLOR } from '../../shared/documentColorDefaults';

type Writable<T> = {
  -readonly [P in keyof T]: Omit<T[P], 'undefined'>;
};

export type AllTextDefaultStylesType = {
  fontFamily: Omit<CSSProperties['fontFamily'], 'undefined'>;
  fontStyle: Omit<CSSProperties['fontStyle'], 'undefined'>;
  textDecoration: Omit<CSSProperties['textDecoration'], 'undefined'>;
  fontWeight: Omit<CSSProperties['fontWeight'], 'undefined'>;
  lineHeight: Omit<CSSProperties['lineHeight'], 'undefined'>;
  color: Omit<CSSProperties['color'], 'undefined'>;
  fontSize: Omit<CSSProperties['fontSize'], 'undefined'>;
  textAlign: Omit<CSSProperties['textAlign'], 'undefined'>;
} & Writable<CSSProperties>;

export enum TextDefaultSelectorsEnum {
  HEADING_1 = 'h1',
  HEADING_2 = 'h2',
  HEADING_3 = 'h3',
  HEADING_4 = 'h4',
  NORMAL = 'p',
}

export enum TableDefaultClassNamesEnum {
  TABLE_BODY = 'table_body_row',
  TABLE_HEADER = 'table_header_row',
  TABLE_FOOTER = 'table_footer_row',
  TABLE_SUBTOTAL = 'table_subtotal_row',
  TABLE_TOTAL = 'table_total_row',
}

export const AllTextDefaultSelectors = {
  ...TextDefaultSelectorsEnum,
  ...TableDefaultClassNamesEnum,
};

export type TextDefaultTagSelectors = `${TextDefaultSelectorsEnum}`;
export type TableTextDefaultTagSelectors = `${TableDefaultClassNamesEnum}`;

export type TextDefaultStylesState = Record<TextDefaultTagSelectors, AllTextDefaultStylesType>;
export type TableTextDefaultStylesState = Record<TableTextDefaultTagSelectors, AllTextDefaultStylesType>;
export type AllTextStyleState = Record<TextDefaultTagSelectors | TableTextDefaultTagSelectors, AllTextDefaultStylesType>;
export type TableDefaultStylesState = Record<TableTextDefaultTagSelectors, AllTextDefaultStylesType>;

const sharedTextStyles: AllTextDefaultStylesType = {
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: `${DEFAULT_SIZE}px`,
  fontStyle: 'normal',
  textDecoration: 'none',
  fontWeight: 'normal',
  lineHeight: DEFAULT_LINE_HEIGHT,
  color: DEFAULT_FONT_COLOR,
  textAlign: 'left',
};

export const sharedTableStyles: AllTextDefaultStylesType = {
  fontSize: `${DEFAULT_TABLE_FONT_SIZE}px`,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontStyle: 'normal',
  textDecoration: 'none',
  fontWeight: 'normal',
  lineHeight: DEFAULT_LINE_HEIGHT,
  color: DEFAULT_FONT_COLOR,
  letterSpacing: 0.15,
  textAlign: 'left',
};

export const textDefaultStyles: TextDefaultStylesState = {
  [TextDefaultSelectorsEnum.NORMAL]: {
    ...sharedTextStyles,
  },
  [TextDefaultSelectorsEnum.HEADING_1]: {
    ...sharedTextStyles,
    fontWeight: 'bold',
    fontSize: `${HEADING_FONT_SIZES[1]}px`,
  },
  [TextDefaultSelectorsEnum.HEADING_2]: {
    ...sharedTextStyles,
    fontWeight: 'bold',
    fontSize: `${HEADING_FONT_SIZES[2]}px`,
  },
  [TextDefaultSelectorsEnum.HEADING_3]: {
    ...sharedTextStyles,
    fontSize: `${HEADING_FONT_SIZES[3]}px`,
  },
  [TextDefaultSelectorsEnum.HEADING_4]: {
    ...sharedTextStyles,
    fontSize: `${HEADING_FONT_SIZES[4]}px`,
  },
};

export const tableTextDefaultStyles: TableTextDefaultStylesState = {
  [TableDefaultClassNamesEnum.TABLE_BODY]: {
    ...sharedTableStyles,
  },
  [TableDefaultClassNamesEnum.TABLE_HEADER]: {
    ...sharedTableStyles,
    fontSize: '16px',
    fontWeight: 'bold',
    color: DEFAULT_TABLE_HEADER_ROW_FONT_COLOR,
  },
  [TableDefaultClassNamesEnum.TABLE_FOOTER]: {
    ...sharedTableStyles,
    fontSize: '16px',
    fontWeight: 'bold',
    color: DEFAULT_TABLE_ROW_FONT_COLOR,
  },
  [TableDefaultClassNamesEnum.TABLE_SUBTOTAL]: {
    ...sharedTableStyles,
    fontWeight: 'bold',
    textAlign: 'right',
    color: DEFAULT_TABLE_ROW_FONT_COLOR,
  },
  [TableDefaultClassNamesEnum.TABLE_TOTAL]: {
    ...sharedTableStyles,
    fontWeight: 'bold',
    textAlign: 'right',
    color: DEFAULT_TABLE_ROW_FONT_COLOR,
  },
};

export enum TextDefaultStyleTypesIdentifier {
  DOCUMENT = 'Document',
  TABLE = 'Table',
}

export type UpdateTextDefaultStylingPayloadType = {
  type: TextDefaultTagSelectors | TableTextDefaultTagSelectors;
  style: Partial<AllTextDefaultStylesType>;
  TextDefaultStyleTypes: TextDefaultStyleTypesIdentifier;
};

const allTextDefaultStylesSlice = createSlice({
  name: 'editor-all-text-default-styles',
  initialState: {
    textDefaultStyles,
    tableTextDefaultStyles,
  },
  reducers: {
    setTextDefaultStyling: (state, action: PayloadAction<TextDefaultStylesState>) => {
      state.textDefaultStyles = {
        ...state.textDefaultStyles,
        ...action.payload,
      };
    },
    setTableTextDefaultStyling: (state, action: PayloadAction<TableTextDefaultStylesState>) => {
      state.tableTextDefaultStyles = {
        ...state.tableTextDefaultStyles,
        ...action.payload,
      };
    },
  },
});

export const { setTextDefaultStyling, setTableTextDefaultStyling } = allTextDefaultStylesSlice.actions;
export const allTextDefaultStylesReducer = allTextDefaultStylesSlice.reducer;

export const selectTextDefaultStylesState = (state: RootState) => state.allTextDefaultStylesReducer;

export const selectAllTextDefaultStyles = createSelector(selectTextDefaultStylesState, (state) => ({
  ...state.textDefaultStyles,
  ...state.tableTextDefaultStyles,
}));

export const selectTextDefaultStyles = (state: RootState) => state.allTextDefaultStylesReducer.textDefaultStyles;
export const selectTableTextDefaultStyles = (state: RootState) => state.allTextDefaultStylesReducer.tableTextDefaultStyles;
